import { Chip } from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClock, faCircleCheck, faCalendarDays } from "@fortawesome/free-solid-svg-icons"
import moment from "moment"
import { faLock } from "@fortawesome/pro-solid-svg-icons"

type CustomChipProps = {
  borderColor?: string
  backgroundColor?: string
  textColor?: string
}

type RecertStatusChipProps = {
  type: number
  date: string | null
}

export const CustomRecertChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "borderColor" && prop !== "backgroundColor" && prop !== "textColor"
})<CustomChipProps>(({ borderColor, backgroundColor, textColor }) => ({
  borderRadius: "4px",
  fontWeight: 500,

  "& .MuiChip-label": {
    paddingRight: "8px !important"
  },

  ...(borderColor && { border: `1px solid ${borderColor}` }),
  ...(backgroundColor && { backgroundColor }),
  ...(textColor && { color: textColor })
}))

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)({
  marginLeft: "8px !important"
})

enum RecertificationStatus {
  NotActive = -1,
  StartingSoon = 2,
  Passed = 1,
  Failed = 0
}

export const RecertStatusChip = ({ type, date }: RecertStatusChipProps): JSX.Element | string => {
  const theme = useTheme()
  const { t } = useTranslation()

  switch (type) {
    case RecertificationStatus.NotActive:
      return (
        <CustomRecertChip
          borderColor={(theme.palette as any).warning[700]}
          backgroundColor={(theme.palette as any).warning[100]}
          textColor={(theme.palette as any).warning[700]}
          variant="outlined"
          color="primary"
          icon={<StyledFontAwesomeIcon size="1x" icon={faLock} color={(theme.palette as any).warning[700]} />}
          label={t("global.inactive", "Inactive")}
        />
      )

    case RecertificationStatus.Failed:
      return (
        <CustomRecertChip
          borderColor={(theme.palette as any).sky[700]}
          backgroundColor={(theme.palette as any).sky[100]}
          textColor={(theme.palette as any).sky[700]}
          variant="outlined"
          color="primary"
          icon={<StyledFontAwesomeIcon size="1x" icon={faClock} color={(theme.palette as any).sky[700]} />}
          label={t("learning.titles.in_progress", "In progress")}
        />
      )

    case RecertificationStatus.Passed:
      return (
        <CustomRecertChip
          borderColor={(theme.palette as any).success[700]}
          backgroundColor={(theme.palette as any).green[200]}
          textColor={(theme.palette as any).success[700]}
          variant="outlined"
          color="success"
          icon={
            <StyledFontAwesomeIcon
              size="1x"
              icon={faCircleCheck}
              color={(theme.palette as any).success[700]}
            />
          }
          label={t("global.done", "Done")}
        />
      )

    case RecertificationStatus.StartingSoon:
      return (
        <CustomRecertChip
          borderColor={(theme.palette as any).sky[700]}
          backgroundColor={(theme.palette as any).sky[100]}
          textColor={(theme.palette as any).sky[700]}
          variant="outlined"
          color="primary"
          icon={
            <StyledFontAwesomeIcon size="1x" icon={faCalendarDays} color={(theme.palette as any).sky[700]} />
          }
          label={t("global.starting", "Starting") + moment(date).format(" YYYY-MM-DD")}
        />
      )

    default:
      return "-"
  }
}

export const recertificationPeriodStatus = (row: number, t: any): string => {
  switch (row) {
    case 0:
      return t("learning.titles.recert_in_progress", "Recertification in progress")
    case 1:
      return t("global.done", "Done")
    case 2:
      return t("global.starting_soon", "Starting")
    default:
      return "-"
  }
}
